import { useEffect, useState } from 'react';
import { endpoints, type SettlementBudgetInput, type SettlementReadiness } from '../api';
import { Assumptions, Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Progress, Section, Stat, Stats, Status, currency, dateTime, number } from '../components';
import { useApiQuery } from '../hooks';

export function SkillsPage() {
  const query = useApiQuery(endpoints.skills);
  const plan = useApiQuery(endpoints.skillPlan);
  const queue = useApiQuery(endpoints.skillQueue);
  return <Page title="Skills">
    <Section title="Cola de entrenamiento">
      {queue.loading && !queue.data && <LoadingState />}
      {queue.error && <ErrorState error={queue.error} onRetry={() => void queue.reload()} />}
      {queue.data && queue.data.length === 0 && <EmptyState title="Sin entrenamiento capturado" detail=" Sincroniza la cola; también puede estar vacía." />}
      {queue.data && queue.data.length > 0 && <div className="table-wrap"><table><thead><tr><th className="num" style={{ width: 40 }}>#</th><th>Skill</th><th className="num">Nivel</th><th>Inicio</th><th>Fin previsto</th></tr></thead><tbody>{[...queue.data].sort((a, b) => a.queue_position - b.queue_position).map((entry) => <tr key={`${entry.queue_position}-${entry.skill_id}`}><td className="num muted">{entry.queue_position + 1}</td><td><strong>{query.data?.find((skill) => skill.skillId === entry.skill_id)?.name ?? `Type ID ${entry.skill_id}`}</strong></td><td className="num">{entry.finished_level}</td><td className="muted">{dateTime(entry.start_date)}</td><td>{dateTime(entry.finish_date)}</td></tr>)}</tbody></table></div>}
    </Section>
    <div className="split">
      <Section title="Skills relevantes">
        {query.loading && !query.data && <LoadingState />}
        {query.error && <ErrorState error={query.error} onRetry={() => void query.reload()} />}
        {query.data && query.data.length === 0 && <EmptyState title="Sin skills sincronizadas" />}
        {query.data && query.data.length > 0 && <div className="table-wrap"><table><thead><tr><th>Skill</th><th>Grupo</th><th className="num">SP</th><th>Nivel</th></tr></thead><tbody>{query.data.map((skill) => <tr key={skill.skillId}>
          <td><strong>{skill.name}</strong></td>
          <td className="muted">{skill.group}</td>
          <td className="num muted">{number(skill.skillpoints)}</td>
          <td><span className="levels" role="img" aria-label={`Nivel ${skill.activeLevel} de 5`}>{[1, 2, 3, 4, 5].map((level) => <i className={level <= skill.activeLevel ? 'on' : undefined} key={level} />)}</span></td>
        </tr>)}</tbody></table></div>}
      </Section>
      <Section title={`Plan${plan.data?.target ? ` · ${plan.data.target}` : ''}`}>
        {plan.loading && !plan.data && <LoadingState />}
        {plan.error && <ErrorState error={plan.error} onRetry={() => void plan.reload()} />}
        {plan.data && <>
          {plan.data.readinessPercent === null ? <p className="muted">Preparación no calculable todavía.</p> : <div className="stack" style={{ gap: 4 }}><span className="mono" style={{ fontSize: 19 }}>{plan.data.readinessPercent.toFixed(0)} %</span><Progress value={plan.data.readinessPercent} /></div>}
          {plan.data.recommendations.length === 0 ? <EmptyState title="Sin recomendaciones" /> : <ul className="feed">{plan.data.recommendations.map((item) => <li key={item.skillId}>
            <strong>{item.name} <span className="mono muted">{item.currentLevel} → {item.targetLevel}</span></strong>
            <small>{item.reason}</small>
            <span className="feed-end"><Status value={item.priority} />{item.estimatedTrainingSeconds !== null && <small>{duration(item.estimatedTrainingSeconds)}</small>}</span>
          </li>)}</ul>}
          <Assumptions items={plan.data.assumptions} />
        </>}
      </Section>
    </div>
  </Page>;
}

export function ExpeditionsPage() {
  const expeditions = useApiQuery(endpoints.expeditions);
  const settlements = useApiQuery(endpoints.settlements);
  const [busy, setBusy] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  async function create(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const element = event.currentTarget;
    const form = new FormData(element);
    setBusy(true); setMessage(null);
    try {
      await endpoints.addExpedition({ name: String(form.get('name')), systemName: String(form.get('systemName')), objective: String(form.get('objective')), budget: Number(form.get('budget')), startsAt: String(form.get('startsAt')), notes: String(form.get('notes')), originSystem: String(form.get('originSystem')) || null, wormholeClass: String(form.get('wormholeClass')) || null, ships: String(form.get('ships')).split(',').map((item) => item.trim()).filter(Boolean) });
      element.reset(); setMessage({ tone: 'success', text: 'Expedición creada.' }); await expeditions.reload();
    } catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo crear' }); }
    finally { setBusy(false); }
  }

  async function toggle(settlementId: string, itemId: string) {
    setMessage(null);
    try { await endpoints.toggleSettlementItem(settlementId, itemId); await settlements.reload(); }
    catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo actualizar' }); }
  }

  async function closeExpedition(event: React.FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setMessage(null);
    try {
      await endpoints.updateExpedition(id, { status: 'completed', durationHours: Number(form.get('durationHours')), lootEstimated: Number(form.get('lootEstimated')), realizedSales: Number(form.get('realizedSales')), replacementCosts: Number(form.get('replacementCosts')) });
      setClosingId(null); setMessage({ tone: 'success', text: 'Expedición cerrada y resultado registrado.' }); await expeditions.reload();
    } catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo cerrar' }); }
    finally { setBusy(false); }
  }

  return <Page title="Expediciones">
    {message && <Note tone={message.tone}>{message.text}</Note>}
    <div className="split">
      <Section title="Planes">
        {expeditions.loading && !expeditions.data && <LoadingState />}
        {expeditions.error && <ErrorState error={expeditions.error} onRetry={() => void expeditions.reload()} />}
        {expeditions.data && expeditions.data.length === 0 && <EmptyState title="Sin expediciones" />}
        {expeditions.data && expeditions.data.length > 0 && <div className="stack" style={{ gap: 24 }}>{expeditions.data.map((item) => <article className="stack" key={item.id}>
          <div className="row"><strong>{item.name}</strong><Status value={item.status} /></div>
          <p className="muted">{item.originSystem ? `${item.originSystem} → ` : ''}{item.systemName}{item.wormholeClass ? ` · ${item.wormholeClass}` : ''} · {item.objective} · {dateTime(item.startsAt)}</p>
          {item.notes && <p className="fine">{item.notes}</p>}
          <dl className="meta">
            <div><dt>Presupuesto</dt><dd>{currency(item.budget)}</dd></div>
            <div><dt>Killmails vinculados</dt><dd>{currency(item.linkedKillmailCosts)}</dd></div>
            <div><dt>Reemplazo total</dt><dd>{currency(item.totalReplacementCosts)}</dd></div>
            <div><dt>Beneficio realizado</dt><dd>{currency(item.realizedProfit)}</dd></div>
            <div><dt>Beneficio estimado</dt><dd>{currency(item.estimatedProfit)}</dd></div>
          </dl>
          {item.killmailIds.length > 0 && <p><a className="link" href="#/killmails">Killmails: {item.killmailIds.map((id) => `#${id}`).join(', ')}</a></p>}
          {item.status !== 'completed' && <div><Button type="button" variant="secondary" onClick={() => setClosingId(closingId === item.id ? null : item.id)}>Registrar cierre</Button></div>}
          {closingId === item.id && <form className="form-grid" onSubmit={(event) => void closeExpedition(event, item.id)}>
            <Field label="Duración (h)"><input required min="0" step="0.1" name="durationHours" type="number" /></Field>
            <Field label="Botín estimado"><input required min="0" name="lootEstimated" type="number" /></Field>
            <Field label="Ventas realizadas"><input required min="0" name="realizedSales" type="number" /></Field>
            <Field label="Otros costes de reemplazo" hint="Sin los killmails vinculados, que se suman solos"><input required min="0" name="replacementCosts" type="number" /></Field>
            <div className="span"><Button disabled={busy}>Completar expedición</Button></div>
          </form>}
        </article>)}</div>}
      </Section>
      <Section title="Nueva expedición">
        <form className="form-grid" onSubmit={create}>
          <Field label="Nombre"><input required maxLength={100} name="name" /></Field>
          <Field label="Objetivo"><input required maxLength={180} name="objective" /></Field>
          <Field label="Origen"><input maxLength={40} name="originSystem" /></Field>
          <Field label="Sistema objetivo"><input required maxLength={40} name="systemName" /></Field>
          <Field label="Clase del agujero"><input maxLength={20} name="wormholeClass" placeholder="C2, C5…" /></Field>
          <Field label="Naves" hint="Separadas por comas"><input maxLength={300} name="ships" /></Field>
          <Field label="Presupuesto (ISK)"><input required min="0" name="budget" type="number" /></Field>
          <Field label="Inicio"><input required name="startsAt" type="datetime-local" /></Field>
          <Field label="Notas"><textarea maxLength={1000} name="notes" rows={3} /></Field>
          <div className="span"><Button disabled={busy}>{busy ? 'Creando…' : 'Crear expedición'}</Button></div>
        </form>
      </Section>
    </div>
    <SettlementBudget />
    <Section title="Preparación de asentamientos">
      {settlements.loading && !settlements.data && <LoadingState />}
      {settlements.error && <ErrorState error={settlements.error} onRetry={() => void settlements.reload()} />}
      {settlements.data && settlements.data.length === 0 && <EmptyState title="Sin asentamientos en preparación" />}
      {settlements.data && settlements.data.length > 0 && <div className="split split--even">{settlements.data.map((settlement) => {
        const percent = settlement.budget > 0 ? Math.min(100, settlement.spent / settlement.budget * 100) : 0;
        const completed = settlement.checklist.filter((item) => item.completed).length;
        return <article className="stack" key={settlement.id}>
          <div className="row"><strong>{settlement.name} <span className="muted">· {settlement.systemName}</span></strong><Status value={settlement.status} /></div>
          <div className="row"><span className="mono">{currency(settlement.spent)} / {currency(settlement.budget)}</span><span className="mono muted">{percent.toFixed(0)} %</span></div>
          <Progress value={percent} />
          <div className="checklist"><small>{completed} de {settlement.checklist.length} comprobaciones</small>{settlement.checklist.map((item) => <button type="button" aria-pressed={item.completed} onClick={() => void toggle(settlement.id, item.id)} key={item.id}>{item.label}</button>)}</div>
        </article>;
      })}</div>}
    </Section>
  </Page>;
}

export function WormholesPage() {
  const query = useApiQuery(endpoints.wormholes);
  const access = useApiQuery(endpoints.accessSettings);
  return <Page title="Agujeros de gusano" actions={<>
    {access.data && <a className="button button--ghost" href={`${access.data.wandererUrl}/maps`} target="_blank" rel="noreferrer">Mis mapas ↗</a>}
    {access.data?.wandererEntryUrl ? <a className="button button--primary" href={access.data.wandererEntryUrl} target="_blank" rel="noreferrer">Entrar en Wanderer ↗</a> : <a className="button button--primary" href="#/admin">Preparar Wanderer</a>}
  </>}>
    {access.error && <ErrorState error={access.error} onRetry={() => void access.reload()} />}
    {access.data && !access.data.wandererEntryUrl && <Note>Falta la entrada privada: la portada de Wanderer oculta el login sin invitación. Guarda la invitación de usuario en <a className="link" href="#/admin">Administración</a>.</Note>}
    <Section title="Conexiones">
      {query.loading && !query.data && <LoadingState />}
      {query.error && <ErrorState error={query.error} onRetry={() => void query.reload()} />}
      {query.data && query.data.length === 0 && <EmptyState title="El mapa vive en Wanderer" detail=" Sistemas, firmas, masa y vida se gestionan allí; aquí no se guarda una copia." />}
      {query.data && query.data.length > 0 && <div className="table-wrap"><table className="table--stack"><thead><tr><th>Origen</th><th>Firma</th><th>Tipo</th><th>Destino</th><th>Masa</th><th>Vida</th></tr></thead><tbody>{query.data.map((connection) => <tr key={connection.id}>
        <td data-label="Origen"><strong>{connection.fromSystem}</strong></td>
        <td className="mono" data-label="Firma">{connection.signature}</td>
        <td data-label="Tipo">{connection.type ?? 'Desconocido'}</td>
        <td data-label="Destino"><strong>{connection.toSystem ?? 'Sin identificar'}</strong></td>
        <td data-label="Masa"><Status value={connection.massStatus} /></td>
        <td data-label="Vida"><Status value={connection.lifeStatus} /></td>
      </tr>)}</tbody></table></div>}
    </Section>
  </Page>;
}

export function AnoikisPage() {
  const query = useApiQuery(endpoints.anoikis);
  return <Page title="Anoikis">
    {query.loading && !query.data && <LoadingState />}
    {query.error && <ErrorState error={query.error} onRetry={() => void query.reload()} />}
    {query.data && <>
      <Stats>
        <Stat label="Sistema base" value={query.data.homeSystem ?? 'Sin configurar'} />
        <Stat label="Conexiones activas" value={number(query.data.activeConnections)} />
        <Stat label="Riesgo" value={<Status value={query.data.riskLevel} />} />
        <Stat label="Cartografía" value={<span style={{ fontSize: 15 }}>{dateTime(query.data.updatedAt)}</span>} />
      </Stats>
      <Section title="Notas de cadena">
        {query.data.notes.length === 0 ? <EmptyState title="Sin notas" /> : <ul className="feed">{query.data.notes.map((note, index) => <li key={index}><span>{note}</span></li>)}</ul>}
      </Section>
    </>}
  </Page>;
}

function duration(seconds: number) {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${number(Number((seconds / 3600).toFixed(1)))} h`;
  return `${number(Number((seconds / 86400).toFixed(1)))} días`;
}

const settlementCosts = [
  ['structure', 'Estructura'], ['core', 'Núcleo'], ['fuel', 'Combustible'],
  ['services', 'Servicios'], ['fitting', 'Equipamiento'], ['hauling', 'Transporte'],
  ['evacuation', 'Evacuación'], ['ships', 'Naves'],
] as const;

function SettlementBudget() {
  const saved = useApiQuery(endpoints.settlementBudget);
  const [form, setForm] = useState<SettlementBudgetInput | null>(null);
  const [result, setResult] = useState<SettlementReadiness | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (saved.data) setForm(saved.data); }, [saved.data]);
  const projectCost = settlementCosts.reduce((sum, [key]) => sum + (form?.costs[key] ?? 0), 0);

  async function evaluate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const costs = Object.fromEntries(settlementCosts.map(([key]) => [key, form.costs[key] ?? 0]));
      const body = { ...form, projectCost: Math.round(projectCost * 100) / 100, costs };
      setResult(await endpoints.saveSettlementBudget(body)); setForm(body);
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'No se pudo guardar el presupuesto'); }
    finally { setBusy(false); }
  }

  return <Section title="Presupuesto de asentamiento">
    <p className="fine">Primero se protegen emergencia, operación y compromisos; después se financia el proyecto y su reserva de reemplazo. Financiar no significa estar preparado.</p>
    {saved.loading && !saved.data && <LoadingState />}
    {saved.error && <ErrorState error={saved.error} onRetry={() => void saved.reload()} />}
    <div className="split">
      {form && <form className="form-stack" onSubmit={evaluate}>
        <div className="form-grid">{([['emergencyReserve', 'Reserva de emergencia'], ['operatingBuffer', 'Colchón operativo'], ['committedCosts', 'Compromisos existentes'], ['replacementReserve', 'Reserva de reemplazo']] as const).map(([key, label]) => <Field key={key} label={`${label} (ISK)`}><input required min="0" step="0.01" type="number" value={form[key]} onChange={(event) => { setForm({ ...form, [key]: Number(event.target.value) }); setResult(null); }} /></Field>)}</div>
        <fieldset><legend>Costes del proyecto (ISK)</legend><div className="form-grid">{settlementCosts.map(([key, label]) => <Field label={label} key={key}><input required min="0" step="0.01" type="number" value={form.costs[key] ?? ''} onChange={(event) => { setForm({ ...form, costs: { ...form.costs, [key]: Number(event.target.value) } }); setResult(null); }} /></Field>)}</div></fieldset>
        <div className="row"><span>Total: <span className="mono">{currency(projectCost)}</span></span><Button disabled={busy}>{busy ? 'Evaluando…' : 'Guardar y evaluar'}</Button></div>
      </form>}
      <div className="stack">
        {error && <Note tone="error">{error}</Note>}
        {!result && !error && <p className="muted">El resultado aparecerá aquí.</p>}
        {result && <>
          <Note tone={result.ready ? 'success' : 'warn'}>{result.ready ? 'Financiable. Completa las comprobaciones operativas.' : 'Mantén salidas de día hasta cubrir proyecto y reserva.'}</Note>
          <dl className="rows">
            <div><dt>Liquidez de referencia</dt><dd>{currency(Number(result.liquid_isk))}</dd></div>
            <div><dt>Capital protegido</dt><dd>{currency(Number(result.protected_capital))}</dd></div>
            <div><dt>Máximo desplegable</dt><dd>{currency(Number(result.max_project_budget))}</dd></div>
            <div><dt>Proyecto + reemplazo</dt><dd>{currency(Number(result.project_required))}</dd></div>
            <div className="total"><dt>Capital pendiente</dt><dd>{currency(Number(result.shortfall))}</dd></div>
          </dl>
          <p className="fine">Fuente: {result.liquidSource}. {result.operationalReadiness}</p>
        </>}
      </div>
    </div>
  </Section>;
}
